import type { DraggableLocation } from '@hello-pangea/dnd';
import type { Bookmark, Folder, UserBookmarks } from '@/lib/bookmark/types';
import type { DraggableItem } from './types';

export function combineItems(
  folders: Folder[],
  bookmarks: Bookmark[]
): DraggableItem[] {
  const items: DraggableItem[] = [
    ...folders.map((f) => ({ ...f, type: 'folder' as const })),
    ...bookmarks.map((b) => ({ ...b, type: 'bookmark' as const })),
  ];
  return items.sort((a, b) => a.order - b.order);
}

export function splitItems(items: DraggableItem[]) {
  return {
    folders: items.filter((item) => item.type === 'folder'),
    bookmarks: items.filter((item) => item.type === 'bookmark'),
  };
}

export function getFolderAtPath(
  root: UserBookmarks,
  path: number[]
): Folder | null {
  if (path.length === 0) {
    return null;
  }

  let current: Folder | undefined = root.folders[path[0]];

  if (!current) {
    return null;
  }

  for (let i = 1; i < path.length; i++) {
    current = current.child_folders?.[path[i]];
    if (!current) {
      return null;
    }
  }

  return current;
}

export function getFoldersAtLevel(
  userBookmarks: UserBookmarks,
  path: number[]
): Folder[] {
  if (path.length === 0) {
    return userBookmarks.folders; // root level
  }
  const folder = getFolderAtPath(userBookmarks, path);
  return folder?.child_folders ? folder.child_folders : [];
}

interface GetContainerAtPathReturn {
  folders: Folder[];
  bookmarks: Bookmark[];
}

export function getContainerAtPath(
  root: UserBookmarks,
  path: number[]
): GetContainerAtPathReturn {
  if (path.length === 0) {
    return {
      folders: root.folders,
      bookmarks: root.bookmarks,
    };
  }

  const folder = getFolderAtPath(root, path);
  if (!folder) {
    return { folders: [], bookmarks: [] };
  }

  return {
    folders: folder.child_folders ?? [],
    bookmarks: folder.child_bookmarks ?? [],
  };
}

function getFolderRefAtPath(
  root: UserBookmarks,
  path: number[]
): Folder | null {
  if (path.length === 0) {
    return null;
  }

  let current = root.folders[path[0]];
  if (!current) {
    return null;
  }

  for (let i = 1; i < path.length; i++) {
    const idx = path[i];
    if (!current.child_folders || !current.child_folders[idx]) {
      return null;
    }
    current = current.child_folders[idx];
  }

  return current;
}

function getContainerRefAtPath(
  root: UserBookmarks,
  path: number[]
): { folders: Folder[]; bookmarks: Bookmark[] } {
  if (path.length === 0) {
    return {
      folders: root.folders,
      bookmarks: root.bookmarks,
    };
  }

  const folder = getFolderRefAtPath(root, path);
  if (!folder) {
    return { folders: [], bookmarks: [] };
  }

  if (!folder.child_folders) {
    folder.child_folders = [];
  }
  if (!folder.child_bookmarks) {
    folder.child_bookmarks = [];
  }

  return {
    folders: folder.child_folders,
    bookmarks: folder.child_bookmarks,
  };
}

export function pathForLevel(openPath: number[], level: number): number[] {
  return openPath.slice(0, level);
}

interface MoveParams {
  userBookmarks: UserBookmarks;
  openPath: number[];
  source: DraggableLocation<string>;
  destination: DraggableLocation<string>;
}

export function moveInTree(props: MoveParams): UserBookmarks {
  const { userBookmarks, openPath, destination, source } = props;
  const sourceLevel = getLevelFromDroppableId(source.droppableId);
  const destLevel = getLevelFromDroppableId(destination.droppableId);
  const sourceIndex = source.index;
  const destinationIndex = destination.index;
  const sourcePath = pathForLevel(openPath, sourceLevel);
  const destPath = pathForLevel(openPath, destLevel);

  const sameContainer = destLevel === sourceLevel;
  const updated: UserBookmarks = structuredClone(userBookmarks);

  const srcRef = getContainerRefAtPath(updated, sourcePath);
  const dstRef = sameContainer
    ? srcRef
    : getContainerRefAtPath(updated, destPath);

  // to combined draggable lists
  const srcItems = combineItems(srcRef.folders, srcRef.bookmarks);
  const dstItems = sameContainer
    ? srcItems
    : combineItems(dstRef.folders, dstRef.bookmarks);

  const [moved] = srcItems.splice(sourceIndex, 1);
  if (!moved) {
    return userBookmarks;
  }

  // Insert into destination
  dstItems.splice(destinationIndex, 0, moved);

  // Normalize order (on the underlying data)
  for (const [i, item] of srcItems.entries()) {
    item.order = i;
  }
  if (!sameContainer) {
    for (const [i, item] of dstItems.entries()) {
      item.order = i;
    }
  }

  // Split back into folders/bookmarks
  const { folders: newSrcFolders, bookmarks: newSrcBookmarks } =
    splitItems(srcItems);

  const { folders: newDstFolders, bookmarks: newDstBookmarks } = sameContainer
    ? { folders: newSrcFolders, bookmarks: newSrcBookmarks }
    : splitItems(dstItems);

  srcRef.folders.length = 0;
  srcRef.folders.push(...newSrcFolders);

  srcRef.bookmarks.length = 0;
  srcRef.bookmarks.push(...newSrcBookmarks);

  if (!sameContainer) {
    dstRef.folders.length = 0;
    dstRef.folders.push(...newDstFolders);

    dstRef.bookmarks.length = 0;
    dstRef.bookmarks.push(...newDstBookmarks);
  }

  return updated;
}

export function getLevelFromDroppableId(id: string): number {
  return parseInt(id.split('-')[1], 10);
}

export function resolveOpenPathIndices(
  bookmarks: UserBookmarks | undefined,
  openPath: string[]
): number[] {
  if (!bookmarks) {
    return [];
  }

  const indices: number[] = [];

  let currentFolders: Folder[] = bookmarks.folders;

  for (const folderId of openPath) {
    const idx = currentFolders.findIndex(
      (folder) => folder.client_id === folderId
    );
    if (idx === -1) {
      // Folder no longer exists at this level → truncate the path
      break;
    }

    indices.push(idx);

    const folder = currentFolders[idx];
    currentFolders = folder.child_folders ?? [];
  }

  return indices;
}

interface DeleteByLevelAndIndexParams {
  readonly userBookmarks: UserBookmarks;
  readonly openPath: number[];
  readonly level: number;
  readonly index: number;
}

interface DeleteResult {
  readonly userBookmarks: UserBookmarks;
  readonly deletedItem: DraggableItem | null;
}

/**
 * Deletes an item by its level and index in the tree.
 */
export function deleteByLevelAndIndex(
  params: DeleteByLevelAndIndexParams
): DeleteResult {
  const { userBookmarks, openPath, level, index } = params;

  const targetPath = pathForLevel(openPath, level);
  const updated: UserBookmarks = structuredClone(userBookmarks);

  const containerRef = getContainerRefAtPath(updated, targetPath);
  const combinedItems = combineItems(
    containerRef.folders,
    containerRef.bookmarks
  );

  const itemToDelete = combinedItems[index];
  if (!itemToDelete) {
    return { userBookmarks, deletedItem: null };
  }

  combinedItems.splice(index, 1);

  for (const [i, item] of combinedItems.entries()) {
    item.order = i;
  }

  const { folders: newFolders, bookmarks: newBookmarks } =
    splitItems(combinedItems);

  containerRef.folders.length = 0;
  containerRef.folders.push(...(newFolders as Folder[]));

  containerRef.bookmarks.length = 0;
  containerRef.bookmarks.push(...(newBookmarks as Bookmark[]));

  return { userBookmarks: updated, deletedItem: itemToDelete };
}

// ============================================================================
// Tree Mutation (Edit / Delete / Create)
// ============================================================================

export function updateItemInTree(
  bookmarks: UserBookmarks,
  path: number[],
  itemId: string,
  updates: Partial<Folder | Bookmark>
): UserBookmarks {
  const updated = structuredClone(bookmarks);
  const containerRef = getContainerRefAtPath(updated, path);

  const combined = combineItems(containerRef.folders, containerRef.bookmarks);
  const item = combined.find((i) => i.client_id === itemId);

  if (item) {
    Object.assign(item, updates);

    // If we need to re-sort or re-split after edit:
    const { folders, bookmarks } = splitItems(combined);
    containerRef.folders.length = 0;
    containerRef.folders.push(...folders);
    containerRef.bookmarks.length = 0;
    containerRef.bookmarks.push(...bookmarks);
  }

  return updated;
}

export function deleteItemInTree(
  bookmarks: UserBookmarks,
  path: number[],
  itemId: string
): UserBookmarks {
  const updated = structuredClone(bookmarks);
  const containerRef = getContainerRefAtPath(updated, path);

  const combined = combineItems(containerRef.folders, containerRef.bookmarks);
  const index = combined.findIndex((i) => i.client_id === itemId);

  if (index !== -1) {
    combined.splice(index, 1);

    // Re-order remaining items
    for (const [i, item] of combined.entries()) {
      item.order = i;
    }

    const { folders, bookmarks } = splitItems(combined);
    containerRef.folders.length = 0;
    containerRef.folders.push(...folders);
    containerRef.bookmarks.length = 0;
    containerRef.bookmarks.push(...bookmarks);
  }

  return updated;
}
